import { useState } from 'react';
import PropTypes from 'prop-types';
import { Helmet } from 'react-helmet';
import {
  Box,
  Container,
  Stack,
  Typography,
  Button,
  useTheme
} from '@mui/material';
import { Add as AddIcon } from '@mui/icons-material';
import FilterSelectInput from 'src/common/FilterSelectInput';
import TankListTable from 'src/components/tanks/TankListTable';
import NewTank from 'src/components/tanks/NewTank';
import useAuth from 'src/context/AuthContext';
import { hasAdminLikeAccess } from 'src/utils/getInitials';
import useAdminSiteFilter from 'src/hooks/useAdminSiteFilter';
import usePersistentListFilters from 'src/hooks/usePersistentListFilters';
import ListRefreshButton from 'src/common/ListRefreshButton';
import ColumnSearch from 'src/common/ColumnSearch';

const Tanks = ({ location }) => {
  const {
    search, site, setSearch, setSite
  } = usePersistentListFilters();
  const theme = useTheme();
  const { getUser } = useAuth();
  const {
    isAdmin,
    siteFilter,
    setSiteFilter,
    siteOptions
  } = useAdminSiteFilter(site);
  const hasAdminAccess = hasAdminLikeAccess(getUser);
  const [openModal, setOpenModal] = useState(false);
  const [searchText, setSearchText] = useState(search);
  const [searchFilters, setSearchFilters] = useState({});
  const [refreshKey, setRefreshKey] = useState(0);

  return (
    <>
      <Helmet>
        <title>Tanks | Retrack FMS</title>
      </Helmet>

      <Box
        className="transaction-style-page transaction-style-duplicate-header"
        sx={{
          backgroundColor: '#FFFFFF',
          minHeight: 'calc(100vh - 64px)',
          height: { xs: 'auto', md: 'calc(100vh - 64px)' },
          overflow: { xs: 'auto', md: 'hidden' },
          py: 2
        }}
      >
        <Container
          maxWidth="xl"
          sx={{
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            minHeight: 0
          }}
        >

          {/* Header Section */}
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            alignItems={{ xs: 'flex-start', md: 'center' }}
            justifyContent="space-between"
            spacing={3}
            sx={{ py: 2, mb: 2, flexShrink: 0 }}
          >
            {/* Left */}
            <Box>
              <Typography
                variant="h4"
                sx={{
                  fontWeight: 800,
                  color: '#111827',
                  letterSpacing: '-0.02em',
                  mb: 0.5
                }}
              >
                TANKS
              </Typography>

              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ fontWeight: 500 }}
              >
                Manage and monitor all fuel tanks across sites.
              </Typography>
            </Box>

            {/* Right */}
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={2}
              useFlexGap
              flexWrap={{ xs: 'nowrap', sm: 'wrap' }}
              sx={{ width: { xs: '100%', md: 'auto' }, flex: { md: 1 } }}
            >
              <ColumnSearch
                fields={[
                  { key: 'name', label: 'Tank Name', defaultChecked: true },
                  { key: 'type', label: 'Tank Type' },
                  { key: 'code', label: 'Tank Code' }
                ]}
                values={searchFilters}
                onApply={(values) => { setSearchFilters(values); setSearchText(''); setSearch(''); }}
                onClear={() => { setSearchFilters({}); setSearchText(''); setSearch(''); }}
                sx={{ width: { xs: '100%', md: 320 } }}
              />
              <ListRefreshButton onRefresh={() => setRefreshKey((value) => value + 1)} />

              {isAdmin && (
                <FilterSelectInput
                  label="Site"
                  name="site"
                  value={siteFilter}
                  data={siteOptions}
                  onInputChange={(value) => { setSiteFilter(value); setSite(value); }}
                  sx={{ width: { xs: '100%', md: 220 }, mr: 0, ml: { md: 'auto' } }}
                />
              )}

              {hasAdminAccess && (
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={() => setOpenModal(true)}
                  sx={{
                    borderRadius: 2,
                    textTransform: 'none',
                    fontWeight: 600,
                    px: 3,
                    ml: { md: isAdmin ? 0 : 'auto' },
                    alignSelf: { xs: 'stretch', md: 'flex-start' },
                    boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                    backgroundColor: theme.palette.primary.main,
                    '&:hover': {
                      backgroundColor: theme.palette.primary.dark,
                      boxShadow: '0 6px 16px rgba(0,0,0,0.15)'
                    }
                  }}
                >
                  Add Tank
                </Button>
              )}
            </Stack>
          </Stack>

          {/* Table Section */}
          <Box
            sx={{
              flex: 1,
              minHeight: 0,
              overflow: 'hidden'
            }}
          >
            <TankListTable
              refreshData={refreshKey || location.state?.fromDashboard}
              searchText={searchText}
              searchFilters={searchFilters}
              siteFilter={siteFilter}
            />
          </Box>

          <NewTank
            open={openModal}
            handleClose={() => setOpenModal(false)}
          />
        </Container>
      </Box>
    </>
  );
};

Tanks.propTypes = {
  location: PropTypes.object
};

export default Tanks;
